import { motion } from "framer-motion";
import { Coffee, MoonStar, Music, CarFront, BadgeCheck } from "lucide-react";
import { useLang } from "../i18n";
import { STORY_IMG } from "../siteData";

const icons = [Coffee, MoonStar, Music, CarFront];

const reveal = {
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
};

export function Story() {
    const { t } = useLang();
    const s = t.story;
    return (
        <section id="story" className="relative mx-auto max-w-7xl px-4 py-28 sm:px-6" data-testid="story-section">
            <motion.div {...reveal} transition={{ duration: 0.7 }}>
                <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#FFB800]">{s.overline}</p>
                <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                    {s.title}
                </h2>
            </motion.div>

            <div className="mt-12 grid gap-5 md:grid-cols-3">
                <motion.div
                    {...reveal}
                    transition={{ duration: 0.7, delay: 0.05 }}
                    className="rounded-3xl border border-white/10 bg-[#13131A] p-8 md:col-span-2"
                >
                    <p className="text-base leading-relaxed text-[#c9c9d4] sm:text-lg">{s.p1}</p>
                    <p className="mt-5 text-base leading-relaxed text-[#8E8EA0]">{s.p2}</p>
                    <p className="mt-5 flex items-start gap-2 text-sm text-[#FFB800]">
                        <BadgeCheck size={17} className="mt-0.5 shrink-0" />
                        {s.p3}
                    </p>
                </motion.div>

                <motion.div
                    {...reveal}
                    transition={{ duration: 0.7, delay: 0.12 }}
                    className="clip-frame group relative overflow-hidden rounded-3xl border border-white/10 min-h-[280px]"
                >
                    <img
                        src={STORY_IMG}
                        alt="Barber at work"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_0%,rgba(11,11,14,0.85)_100%)]" />
                    <p className="absolute bottom-5 left-5 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">
                        The Chair · 01:00 AM
                    </p>
                </motion.div>

                <motion.div
                    {...reveal}
                    transition={{ duration: 0.7, delay: 0.18 }}
                    className="rounded-3xl border border-white/10 bg-[#13131A] p-8"
                >
                    <h3 className="font-display text-xl font-semibold text-[#FFB800]">{s.card2t}</h3>
                    <ul className="mt-5 space-y-3.5">
                        {s.card2items.map((item, i) => {
                            const Icon = icons[i % icons.length];
                            return (
                                <li key={item} className="flex items-center gap-3 text-sm text-[#c9c9d4]">
                                    <Icon size={16} className="text-[#FF2E4D]" />
                                    {item}
                                </li>
                            );
                        })}
                    </ul>
                </motion.div>

                <motion.div
                    {...reveal}
                    transition={{ duration: 0.7, delay: 0.24 }}
                    className="relative overflow-hidden rounded-3xl border border-[#FFB800]/25 bg-gradient-to-br from-[#181824] to-[#0B0B0E] p-8 md:col-span-2"
                >
                    <span aria-hidden className="pointer-events-none absolute -right-6 -top-10 select-none font-display text-[10rem] font-extrabold leading-none text-stroke-amber opacity-30">
                        9
                    </span>
                    <h3 className="font-display text-xl font-semibold">{s.card3t}</h3>
                    <p className="mt-1 font-mono2 text-xs uppercase tracking-[0.25em] text-[#FFB800]">{s.card3role}</p>
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#8E8EA0]">{s.card3d}</p>
                </motion.div>
            </div>
        </section>
    );
}
